import type { operations } from "@/openapi.gen.ts";
import { $api, fetchClient } from "@/utils/fetch.ts";
import {
	MCP_AUTHORIZATION_RETURN_URL,
	type McpAuthorizationRouting,
} from "@/utils/oauth-state.ts";

/**
 * `mine`: the servers the caller is an admin of (their own connections).
 * `org`: every server of the organization, for the admin page.
 */
export type McpServersScope = "mine" | "org";

/** One server as the list returns it. */
export type McpServerListItem =
	operations["api_mcp-servers_get"]["responses"][200]["content"]["application/json"]["servers"][number];

export const mcpServersQueryOptions = (scope: McpServersScope) =>
	$api.queryOptions("get", "/api/mcp-servers", {
		params: { query: { scope } },
	});

export const useCreateMcpServerMutation = () =>
	$api.useMutation("post", "/api/mcp-servers");

export const useUpdateMcpServerMutation = () =>
	$api.useMutation("put", "/api/mcp-servers/{id}");

export const useDeleteMcpServerMutation = () =>
	$api.useMutation("delete", "/api/mcp-servers/{id}");

export const useTestMcpServerMutation = () =>
	$api.useMutation("post", "/api/mcp-servers/{id}/test");

export const useRefreshMcpServerMutation = () =>
	$api.useMutation("post", "/api/mcp-servers/{id}/refresh-tools");

export const useCompleteMcpServerAuthorizationMutation = () =>
	$api.useMutation("post", "/api/mcp-servers/{id}/authorize/callback");

/**
 * Begin authorizing an OAuth MCP server; resolves to the consent URL. The
 * consent always returns to the user's integrations page, which completes it
 * (`McpAuthorizationReturn`) and then goes `backTo` — the page it started on.
 */
export const startMcpServerAuthorization = async (
	id: string,
	backTo: string,
): Promise<string> => {
	const routing: McpAuthorizationRouting = {
		returnUrl: MCP_AUTHORIZATION_RETURN_URL,
		mcpServerId: id,
		backTo,
	};
	const { data, error } = await fetchClient.POST(
		"/api/mcp-servers/{id}/authorize",
		{ params: { path: { id } }, body: { state: routing } },
	);
	if (error || !data) {
		throw error ?? new Error("Failed to start the MCP server authorization.");
	}
	return data.authorizationUrl;
};
